import React from "react"
import { twMerge } from "tailwind-merge"

import { Divider } from "../../components/shared/Layout"
import PracticeReviewsProfile from "../../components/shared/PracticeReviewsProfile"
import { ToastProvider, useToast } from "../../contexts/ToastContext"
import GraphQLProvider from "../../providers/GraphQLProvider"
import ChatWidget from "../ProProfileShared/ChatWidget"
import Footer from "../ProProfileShared/Footer"
import Navigation from "../ProProfileShared/Navigation"
import ThemeFiveNavigation from "../ProProfileThemeFive/ThemeFiveNavigation"

import BookableEventTypeDetails from "./BookableEventTypeDetails"
import BookingSection from "./BookingSection"
import ExpandableDescription from "./ExpandableDescription"
import FreeDiscoveryCallBlock from "./FreeDiscoveryCallBlock"
import LocationSection from "./LocationSection"
import MeetPractitionerSection from "./MeetPractitionerSection"

const ProProfileBookableEventTypePage = ({ practice, bookableEventType, currentUser, bookableEvents, themeV2 }) => {
  const toast = useToast()

  // Build a normalized theme object that supports both v1 and v2 structures with safe defaults
  const baseTheme = themeV2 || practice?.theme || {}
  const normalizedTheme = {
    // Name
    name: baseTheme?.name || "Default",
    // Colors (prefer v2 structure, then v1, then fallbacks)
    colors: baseTheme?.colors || {
      primary: baseTheme?.colorPrimary || "#4A90E2",
      primaryDark: baseTheme?.colorPrimaryDark || "#357ABD",
      secondary: baseTheme?.colorSecondary || "#F8F8F8",
      secondaryDark: baseTheme?.colorSecondaryDark || "#EDEDED",
      accent: baseTheme?.colorAccent || "#F5A623",
      textLight: baseTheme?.colorTextLight || "#FFFFFF",
      textDark: baseTheme?.colorTextDark || "#333333",
      bgLight: baseTheme?.colorBgLight || "#FFFFFF",
      bgDark: baseTheme?.colorBgDark || "#F8F8F8",
      white: baseTheme?.colorWhite || "#FFFFFF",
      greyLightest: baseTheme?.colorGreyLightest || "#F8F8F8",
      greyLight: baseTheme?.colorGreyLight || "#E0E0E0",
      grey: baseTheme?.colorGrey || "#9E9E9E",
      greyDark: baseTheme?.colorGreyDark || "#616161",
      greyDarkest: baseTheme?.colorGreyDarkest || "#424242",
      black: baseTheme?.colorBlack || "#000000",
      reviewStars: baseTheme?.colorReviewStars || "#FFD700"
    },
    // Fonts
    fonts: baseTheme?.fonts || {
      heading: baseTheme?.fontHeading || "Inter",
      paragraph: baseTheme?.fontParagraph || "Inter",
      headingWeight: baseTheme?.fontHeadingWeight || "600",
      paragraphRegularWeight: baseTheme?.fontParagraphRegularWeight || "400",
      paragraphBoldWeight: baseTheme?.fontParagraphBoldWeight || "600"
    },
    // Shape
    buttonStyle: baseTheme?.buttonStyle || "rounded",
    borderRadius: baseTheme?.borderRadius || "12px",
    // Header and Footer (preserve from baseTheme for v2 sites)
    header: baseTheme?.header || {},
    footer: baseTheme?.footer || {}
  }

  const theme = normalizedTheme
  const themeName = theme.name

  const bookableEventTypeHasImage = Boolean(
    bookableEventType.filestackPhoto && Object.keys(bookableEventType.filestackPhoto).length > 0
  )

  const NavigationComponent = () => {
    if (themeName === "vitality") {
      return <ThemeFiveNavigation practice={practice} theme={theme} />
    }

    if (themeName === "serenity") {
      return <Navigation practice={practice} theme={theme} color={theme.colorTextDark} />
    }

    return <Navigation practice={practice} theme={theme} color={theme.colorTextDark} />
  }

  return (
    <GraphQLProvider>
      <ToastProvider>
        <main className="bg-white md:overflow-x-hidden">
          <div className="relative">
            {/* Centered Title */}
            <div
              className="relative flex min-h-[500px] items-center justify-center text-center sm:min-h-[300px]"
              style={{ backgroundColor: theme.colors?.secondary }}>
              <h1
                className={twMerge(
                  "mb-[200px] mt-16 text-[56px] leading-[72px] text-gray-dark lg:text-4xl lg:leading-[48px] md:mt-[5rem] sm:mb-[100px]"
                )}
                style={{
                  fontFamily: theme.fonts?.heading,
                  fontWeight: theme.fonts?.headingWeight,
                  color: theme.colors?.textDark
                }}>
                {bookableEventType.name}
              </h1>
              <NavigationComponent />
            </div>

            {/* Hero Image Section with Navigation Arrows */}
            <div className="container relative mx-auto mt-[-240px] max-w-3xl px-4 sm:mt-[-120px]">
              {/* Hidden swipe arrows until multi-photo is implemented */}
              {/* <button className="absolute left-6 top-1/2 z-10 -translate-y-1/2 text-gray-dark" aria-label="Previous">
                <span className="text-4xl">&lt;</span>
              </button> */}

              {bookableEventTypeHasImage ? (
                <img
                  src={`https://cdn.filestackcontent.com/${bookableEventType.filestackPhoto.original.any}`}
                  alt={bookableEventType.name}
                  className="h-[480px] w-full rounded-lg object-cover sm:h-60"
                />
              ) : (
                <div className="flex items-center justify-center rounded-lg bg-gray-light px-4 py-32">
                  <h2 className="text-xl font-bold text-[#5f6c7b]">{bookableEventType.name}</h2>
                </div>
              )}

              {/* <button className="absolute right-6 top-1/2 z-10 -translate-y-1/2 text-gray-dark" aria-label="Next">
                <span className="text-4xl">&gt;</span>
              </button> */}
            </div>
          </div>

          <div className="mx-auto w-full max-w-3xl px-4 pb-32 pt-10 md:pb-10">
            {/* Reviewer Profile Section */}
            <div className="mb-8 flex justify-center">
              <PracticeReviewsProfile practice={practice} />
            </div>

            {/* Service Details Section */}
            <div className="mb-8">
              <BookableEventTypeDetails bookableEventType={bookableEventType} practice={practice} theme={theme} />
            </div>

            {/* Details Section */}
            {bookableEventType.description && (
              <>
                <div className="mb-8">
                  <ExpandableDescription
                    content={bookableEventType.description}
                    maxLines={4}
                    theme={theme}
                    title="Details"
                    charLimit={400}
                  />
                </div>

                <Divider />
              </>
            )}

            {/* Location Section */}
            {bookableEventType.kind === "in_person" && bookableEventType.location && (
              <>
                <div className="mb-8">
                  <LocationSection bookableEventType={bookableEventType} theme={theme} />
                </div>

                <Divider />
              </>
            )}

            {/* Booking Section */}
            <BookingSection
              bookableEventType={bookableEventType}
              practice={practice}
              bookableEvents={bookableEvents}
              theme={theme}
            />

            {/* Meet the Practitioner Section */}
            <div className="mb-10 mt-12">
              <MeetPractitionerSection practice={practice} />
            </div>

            {/* Free Discovery Call Block */}
            <FreeDiscoveryCallBlock practice={practice} theme={theme} />
          </div>

          <Footer practice={practice} theme={theme} />
          <ChatWidget practice={practice} currentUser={currentUser} toast={toast} />
        </main>
      </ToastProvider>
    </GraphQLProvider>
  )
}

export default ProProfileBookableEventTypePage
